import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { fieldLimit, firstLengthError } from "@/lib/fieldLimits";
import { useLang } from "@/context/LanguageContext";
import { PageHeader } from "@/components/Layout";
import { initials, statusMeta, byId } from "@/lib/format";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Save, Plus, X, Power, PowerOff, Trash2, CalendarClock, ShieldAlert, Cloud, KeyRound, Loader2, Network, Users2, ChevronDown, Layers, Tag, AlertTriangle, Eye, EyeOff, RefreshCw, Sparkles } from "lucide-react";
import { AiResultDialog, useAiRunner } from "@/components/ai/AiResultDialog";
import { ResetPasswordDialog } from "@/components/employee/ResetPasswordDialog";
import { ReissueTapDialog } from "@/components/employee/ReissueTapDialog";
import { RoleTemplateApplyDialog } from "@/components/employee/RoleTemplateApplyDialog";
import { MemberSection } from "@/components/employee/MemberSection";
import { EmployeePicker } from "@/components/EmployeePicker";
import { EmailAliasManager } from "@/components/employee/EmailAliasManager";

const ATTR_FIELDS = [
  ["displayName", "field.displayName"], ["givenName", "field.givenName"], ["surname", "field.surname"],
  ["userPrincipalName", "field.upn"],
  ["jobTitle", "field.jobTitle"], ["department", "field.department"], ["companyName", "field.company"],
  ["officeLocation", "field.officeNumber"], ["mobilePhone", "field.mobile"],
  ["streetAddress", "field.street"], ["postalCode", "field.zip"], ["city", "field.city"],
  ["usageLocation", "field.usageLocation2"], ["employeeId", "field.employeeId"],
];

const REQUIRED_KEYS = new Set(["displayName", "givenName", "surname"]);


export default function EmployeeDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t, lang } = useLang();
  const { can, ai } = useAuth();
  const aiRunner = useAiRunner();
  const aiOn = !!(ai?.available && ai?.canUse);
  const [user, setUser] = useState(null);
  const [form, setForm] = useState({});
  const [groups, setGroups] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [teams, setTeams] = useState([]);
  const [apps, setApps] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);
  const [hybrid, setHybrid] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [reissueOpen, setReissueOpen] = useState(false);
  const [tapEnabled, setTapEnabled] = useState(false);
  const [roleOpen, setRoleOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [domains, setDomains] = useState([]);
  const [upnState, setUpnState] = useState({ checking: false, available: null, reason: null });
  const [samState, setSamState] = useState({ checking: false, available: null, source: null });
  const [drift, setDrift] = useState(null);
  const [locations, setLocations] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [reqFields, setReqFields] = useState([]);
  const [cloudDescEnabled, setCloudDescEnabled] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyRes, setVerifyRes] = useState(null);
  useEffect(() => { api.get("/usage-locations").then((r) => setLocations(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/companies").then((r) => setCompanies(r.data || [])).catch(() => {}); }, []);
  const [offOpen, setOffOpen] = useState(false);
  const [off, setOff] = useState({
    mode: "immediate", leaveDate: "", blockSignIn: true, removeLicenses: true,
    removeFromGroups: true, removeFromTeams: true, delete: false,
  });

  const load = useCallback(async () => {
    const [u, g, l, tmRes] = await Promise.all([
      api.get(`/users/${id}`), api.get("/groups"), api.get("/licenses"), api.get("/teams"),
    ]);
    setUser(u.data);
    setForm(u.data);
    setGroups(g.data);
    setLicenses(l.data);
    setTeams(tmRes.data);
    api.get("/applications").then((r) => setApps(r.data)).catch(() => {});
    api.get("/users").then((r) => setOrgUsers(r.data.filter((x) => x.id !== id))).catch(() => {});
    api.get("/config").then((r) => { setHybrid(r.data.operationMode === "hybrid"); setReqFields(r.data.requiredFields || []); setCloudDescEnabled(!!r.data.cloudUserDescription?.enabled); setTapEnabled(r.data.operationMode === "cloudOnly" && !!r.data.tap?.enabled); }).catch(() => {});
    api.get("/domains").then((r) => setDomains(r.data)).catch(() => {});
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const origUpn = user?.userPrincipalName || "";
  useEffect(() => {
    const upn = form.userPrincipalName || "";
    if (!upn || upn.toLowerCase() === origUpn.toLowerCase()) {
      setUpnState({ checking: false, available: null, reason: null });
      return;
    }
    setUpnState((s) => ({ ...s, checking: true }));
    const h = setTimeout(() => {
      api.get(`/validate/upn?upn=${encodeURIComponent(upn)}&excludeId=${id}`)
        .then((r) => setUpnState({ checking: false, available: r.data.available, reason: r.data.reason }))
        .catch(() => setUpnState({ checking: false, available: null, reason: null }));
    }, 400);
    return () => clearTimeout(h);
  }, [form.userPrincipalName, origUpn, id]);

  const origSam = user?.onPremisesSamAccountName || "";
  useEffect(() => {
    const sam = form.onPremisesSamAccountName || "";
    if (user?.isCloudOnly || !sam || sam.toLowerCase() === origSam.toLowerCase()) {
      setSamState({ checking: false, available: null, source: null });
      return;
    }
    setSamState((s) => ({ ...s, checking: true }));
    const h = setTimeout(() => {
      api.get(`/validate/sam?sam=${encodeURIComponent(sam)}&excludeId=${id}`)
        .then((r) => setSamState({ checking: false, available: r.data.available, source: r.data.source }))
        .catch(() => setSamState({ checking: false, available: null, source: null }));
    }, 400);
    return () => clearTimeout(h);
  }, [form.onPremisesSamAccountName, origSam, id, user?.isCloudOnly]);

  const syncing = !!user?._syncingUntil && new Date(user._syncingUntil) > new Date();

  // Reconcile drift status for the header badge (admin + on-prem users only; silent on 403).
  useEffect(() => {
    if (!user || user.isCloudOnly) { setDrift(null); return; }
    api.get(`/reconcile/user-drift/${id}`).then(({ data }) => setDrift(data)).catch(() => setDrift(null));
  }, [id, user, user?.isCloudOnly]);
  useEffect(() => {
    if (!syncing) return;
    const iv = setInterval(() => { load(); }, 6000);
    return () => clearInterval(iv);
  }, [syncing, load]);

  if (!user)
    return (<><PageHeader title={t("nav.employees")} /><div className="p-10 font-mono text-xs uppercase tracking-widest text-zinc-400">{t("common.loading")}</div></>);

  const groupMap = byId(groups);
  const licMap = byId(licenses, "skuId");
  const teamMap = byId(teams);
  const st = statusMeta(user.status);
  const gaProtected = !!user.isGlobalAdmin;
  const canManage = can("employees.manage");
  const canEnableDisable = canManage || can("employees.enableDisable");
  const canReset = can("passwordReset");
  const canOffboard = can("offboarding");
  const readOnly = gaProtected || !canManage;
  const canLifecycle = !gaProtected && (canEnableDisable || canReset || canOffboard);

  const isM365 = (g) => g && g.classification === "m365_group";
  const isTeam = (g) => g && (g.classification === "cloud_teams" || g.category === "teams");
  const isDyn = (g) => g && (g.dynamicMembership || g.dynamic);
  const secGroups = groups.filter((g) => g.category !== "distribution" && !isM365(g) && !isTeam(g) && !isDyn(g) && !g.applicationId);
  const m365Groups = groups.filter((g) => isM365(g) && !isDyn(g) && !g.applicationId);
  const distGroups = groups.filter((g) => g.category === "distribution" && !isDyn(g) && !g.applicationId);
  const memberOf = user.memberOf || [];
  const userSec = memberOf.filter((gid) => groupMap[gid] && groupMap[gid].category !== "distribution" && !isM365(groupMap[gid]) && !isTeam(groupMap[gid]) && !isDyn(groupMap[gid]));
  const userM365 = memberOf.filter((gid) => isM365(groupMap[gid]) && !isDyn(groupMap[gid]));
  const userDist = memberOf.filter((gid) => groupMap[gid] && groupMap[gid].category === "distribution" && !isDyn(groupMap[gid]));
  const userDynamic = memberOf.filter((gid) => isDyn(groupMap[gid]) && !isTeam(groupMap[gid]));

  const appMap = Object.fromEntries(apps.map((a) => [a.id, { ...a, displayName: a.name }]));
  const userAppIds = apps.filter((a) => a.groups.length && a.groups.every((gid) => memberOf.includes(gid))).map((a) => a.id);
  const availApps = apps.filter((a) => !userAppIds.includes(a.id)).map((a) => ({ ...a, displayName: a.name }));

  const licDisplay = (licenses || []).map((l) => ({ ...l, id: l.skuId }));
  // Group-based licensing: SKUs delivered through a licensing group can't be assigned directly to a
  // user (only via group membership) — hide them from the direct "add license" picker (mirrors onboarding).
  const groupManagedSkus = new Set(
    (groups || []).filter((g) => g.classification === "licensing")
      .flatMap((g) => (g.assignedLicenses || []).map((a) => (typeof a === "string" ? a : a?.skuId)))
      .filter(Boolean));

  const saveAttrs = async () => {
    const upnPrefix = (form.userPrincipalName || "").split("@")[0];
    const REQ_LABELS = { companyName: "field.company", department: "field.department", jobTitle: "field.jobTitle", manager: "field.manager", mobilePhone: "field.mobile", officeLocation: "field.officeNumber", streetAddress: "field.street", postalCode: "field.zip", city: "field.city" };
    const missing = [
      ...(!(form.displayName || "").trim() ? ["field.displayName"] : []),
      ...(!(form.givenName || "").trim() ? ["field.givenName"] : []),
      ...(!(form.surname || "").trim() ? ["field.surname"] : []),
      ...(!upnPrefix.trim() ? ["field.upn"] : []),
      ...((!user.isCloudOnly && !(form.onPremisesSamAccountName || "").trim()) ? ["field.samAccountName"] : []),
      ...reqFields.filter((k) => k === "manager" ? !form.managerId : !String(form[k] || "").trim()).map((k) => REQ_LABELS[k]).filter(Boolean),
    ];
    if (missing.length) {
      toast.error(`${t("onb.stillNeeded")}: ${missing.map((k) => t(k)).join(", ")}`); return;
    }
    if (upnState.available === false) { toast.error(upnState.reason === "prefix" ? t("onb.prefixTaken") : t("onb.upnTaken")); return; }
    if (!user.isCloudOnly && samState.available === false) { toast.error(t("onb.samTaken")); return; }
    const ATTR_LABELS = { displayName: "field.displayName", givenName: "field.givenName", surname: "field.surname",
      jobTitle: "field.jobTitle", department: "field.department", companyName: "field.company",
      officeLocation: "field.officeNumber", mobilePhone: "field.mobile", streetAddress: "field.street",
      postalCode: "field.zip", city: "field.city" };
    const lenErr = firstLengthError([
      ...ATTR_FIELDS.map(([k]) => [t(ATTR_LABELS[k] || k), form[k], k]),
      [t("field.upn"), form.userPrincipalName, "userPrincipalName"],
      [t("field.samAccountName"), form.onPremisesSamAccountName, "samAccountName"],
      [t("field.description"), form.description, "description"],
    ]);
    if (lenErr) { toast.error(lenErr); return; }
    setSaving(true);
    try {
      const changes = {};
      ATTR_FIELDS.forEach(([k]) => { if (form[k] !== user[k]) changes[k] = form[k]; });
      if ((form.managerId || "") !== (user.managerId || "")) changes.manager = form.managerId || "";
      if (!user.isCloudOnly && (form.onPremisesSamAccountName || "") !== (user.onPremisesSamAccountName || "")) changes.samAccountName = form.onPremisesSamAccountName || "";
      const descEditable = !user.isCloudOnly || cloudDescEnabled;
      if (descEditable && (form.description || "") !== (user.description || "")) changes.description = form.description || "";
      const origProx = JSON.stringify(user.proxyAddresses || []);
      const newProx = JSON.stringify(form.proxyAddresses || []);
      if (newProx !== origProx && (form.proxyAddresses || []).length) changes.proxyAddresses = form.proxyAddresses;
      // mailNickname is always derived automatically from the primary email address.
      const primaryEmail = (form.proxyAddresses || []).find((p) => p.startsWith("SMTP:"))?.slice(5) || form.mail || user.mail || "";
      if (primaryEmail.includes("@")) {
        const nick = primaryEmail.split("@")[0].toLowerCase();
        if (nick && nick !== (user.mailNickname || "")) changes.mailNickname = nick;
      }
      await api.patch(`/users/${id}`, changes);
      toast.success(t("det.attrsUpdated"), { description: "Change dispatched (updateUser)." });
      await load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const mut = async (fn, msg) => {
    try { await fn(); toast.success(msg); await load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const toggleGal = async (hidden) => {
    setUser((u) => ({ ...u, hiddenFromGal: hidden }));
    try { await api.patch(`/users/${id}/gal-visibility`, { hidden }); toast.success(t("det.galToggled")); }
    catch (e) { toast.error(apiErr(e)); setUser((u) => ({ ...u, hiddenFromGal: !hidden })); }
  };

  const toggleVisible = async (v) => {
    try {
      await api.patch(`/users/${id}/visibility`, { isVisible: v });
      setUser((u) => ({ ...u, isVisible: v }));
      toast.success(v ? t("det.nowVisible") : t("det.nowHidden"));
    } catch (e) { toast.error(apiErr(e)); }
  };

  const verifyAd = async () => {
    setVerifying(true); setVerifyRes(null); setVerifyOpen(true);
    try {
      const { data } = await api.post(`/users/${id}/verify-ad`);
      const jobId = data.jobId;
      for (let i = 0; i < 12; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const { data: job } = await api.get(`/agent/admin/jobs/${jobId}`);
        if (job.status === "finished") {
          const attrs = job.result?.attributes;
          if (attrs && Object.keys(attrs).length) setVerifyRes({ attributes: attrs });
          else setVerifyRes({ note: job.message || t("det.verifyNoAttrs"), raw: job.result });
          return;
        }
        if (job.status === "error" || job.status === "stopped") { setVerifyRes({ error: job.message || "Agent error" }); return; }
      }
      setVerifyRes({ error: t("det.verifyTimeout") });
    } catch (e) { setVerifyRes({ error: apiErr(e) }); }
    finally { setVerifying(false); }
  };

  const toggleEnable = () =>
    user.status === "disabled"
      ? mut(() => api.post(`/users/${id}/enable`), t("det.userEnabled"))
      : mut(() => api.post(`/users/${id}/disable`), t("det.userDisabled"));

  const submitOffboard = async () => {
    try {
      const res = await api.post(`/users/${id}/offboard`, off);
      toast.success(off.mode === "scheduled" ? t("det.offboardScheduled") : t("det.userOffboarded"), {
        description: res.data.deleted ? "Account deleted." : "Dispatched to on-prem agent / Graph.",
      });
      setOffOpen(false);
      if (res.data.deleted) nav("/employees");
      else await load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <>
      <PageHeader title={user.displayName} subtitle={user.userPrincipalName}>
        {aiOn && (
          <Button variant="outline" onClick={() => aiRunner.run(t("ai.employeeTitle"), "/ai/explain-employee", { userId: id, language: lang })}
            data-testid="ai-explain-employee" className="rounded-sm border-primary/30 bg-primary/5 text-primary hover:bg-primary/10">
            <Sparkles size={15} className="mr-2" /> {t("ai.explainEmployee")}
          </Button>
        )}
        <Button variant="ghost" onClick={() => nav("/employees")} className="rounded-sm" data-testid="back-button">
          <ArrowLeft size={16} className="mr-1" /> {t("det.back")}
        </Button>
      </PageHeader>

      <div className="p-6 md:p-10">
        {readOnly && (
          <div className="mb-6 flex items-center gap-3 border border-amber-300 bg-amber-50 px-5 py-3" data-testid="global-admin-banner">
            <ShieldAlert size={18} className="shrink-0 text-amber-600" />
            <div className="text-sm text-amber-900">
              <span className="font-bold">{t("det.gaProtectedTitle")}</span> — {t("det.gaProtectedDesc")}
            </div>
          </div>
        )}
        {/* Identity card */}
        <div className="mb-6 flex items-center gap-4 border border-zinc-200 bg-white p-5">
          <div className="flex h-14 w-14 items-center justify-center bg-zinc-900 text-lg font-bold text-white">
            {initials(user.displayName)}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h2 className="font-display text-xl font-bold tracking-tight text-zinc-950">{user.displayName}</h2>
              <span className={`border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${st.cls}`}>{st.label}</span>
              {syncing && !user.cloudSyncPending && <span data-testid="detail-sync-pill" title={t("sync.pillTitle")} className="inline-flex items-center gap-1 border border-sky-300 bg-sky-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-sky-700"><Loader2 size={11} className="animate-spin" /> {t("sync.pill")}</span>}
              {user.cloudSyncPending && <span data-testid="detail-cloud-sync-pill" title={t("sync.cloudPillTitle")} className="inline-flex items-center gap-1 border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700"><Cloud size={11} className="animate-pulse" /> {t("sync.cloudPill")}</span>}
              {hybrid && user.isCloudOnly && <span data-testid="detail-cloud-badge" title={t("emp.cloudOnlyTitle")} className="inline-flex items-center gap-1 border border-sky-300 bg-sky-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-sky-700"><Cloud size={11} /> {t("emp.cloudOnlyBadge")}</span>}
              {drift?.enabled && drift?.samMismatch && <Link to="/settings?tab=sync" data-testid="detail-sam-drift-badge" title={`AD=${drift.samMismatch.samAccountName} / Entra=${drift.samMismatch.entraSamAccountName} · ${t("emp.samDriftTitle")}`} className="inline-flex items-center gap-1 border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700 hover:bg-amber-100"><Tag size={11} /> {t("emp.samDriftBadge")}</Link>}
              {drift?.enabled && drift?.orphaned && <Link to="/settings?tab=sync" data-testid="detail-orphan-badge" title={t("emp.orphanTitle")} className="inline-flex items-center gap-1 border border-rose-300 bg-rose-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-rose-700 hover:bg-rose-100"><AlertTriangle size={11} /> {t("emp.orphanBadge")}</Link>}
            </div>
            <div className="mt-0.5 text-sm text-zinc-500">{user.jobTitle} · {user.department}</div>
          </div>
          {user.leaveDate && (
            <div className="border border-amber-200 bg-amber-50 px-3 py-2 text-right">
              <div className="font-mono text-[10px] uppercase tracking-wider text-amber-700">{t("det.leaveDate")}</div>
              <div className="font-mono text-sm font-bold text-amber-900">{user.leaveDate}</div>
            </div>
          )}
        </div>

        <Tabs defaultValue="attributes">
          <TabsList className="rounded-sm bg-zinc-100">
            <TabsTrigger value="attributes" data-testid="tab-attributes">{t("det.tabAttributes")}</TabsTrigger>
            <TabsTrigger value="access" data-testid="tab-access">{t("det.tabAccess")}</TabsTrigger>
            <TabsTrigger value="org" data-testid="tab-org">{t("det.tabOrg")}</TabsTrigger>
            <TabsTrigger value="danger" data-testid="tab-danger">{t("det.tabLifecycle")}</TabsTrigger>
          </TabsList>

          {/* Attributes */}
          <TabsContent value="attributes" className="mt-6">
            <div className="border border-zinc-200 bg-white p-6">
              <p className="mb-4 text-xs text-zinc-400" data-testid="required-hint">{t("det.requiredHint")}</p>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                {ATTR_FIELDS.filter(([k]) => k !== "userPrincipalName").map(([k, label]) => {
                  const isReq = REQUIRED_KEYS.has(k) || reqFields.includes(k);
                  return (
                  <div key={k} className="space-y-1.5">
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t(label)}{isReq && <span className="text-red-500" data-testid={`req-${k}`}> *</span>}</Label>
                    {k === "usageLocation" ? (
                      <Select value={form[k] || ""} onValueChange={(v) => setForm({ ...form, [k]: v })} disabled={readOnly}>
                        <SelectTrigger data-testid={`attr-${k}`} className="rounded-sm border-zinc-300 focus:ring-2 focus:ring-primary disabled:bg-zinc-50 disabled:text-zinc-400"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>{locations.map((l) => <SelectItem key={l.code} value={l.code}>{l.code} — {l.name}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : k === "companyName" && companies.length > 0 ? (
                      <Select value={form[k] || ""} onValueChange={(v) => setForm({ ...form, [k]: v })} disabled={readOnly}>
                        <SelectTrigger data-testid={`attr-${k}`} className="rounded-sm border-zinc-300 focus:ring-2 focus:ring-primary disabled:bg-zinc-50 disabled:text-zinc-400"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          {form[k] && !companies.some((c) => c.name === form[k]) && <SelectItem value={form[k]}>{form[k]}</SelectItem>}
                          {companies.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        value={form[k] || ""}
                        maxLength={fieldLimit(k)}
                        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                        disabled={readOnly}
                        data-testid={`attr-${k}`}
                        className="rounded-sm border-zinc-300 focus-visible:ring-2 focus-visible:ring-primary disabled:bg-zinc-50 disabled:text-zinc-400"
                      />
                    )}
                  </div>
                  );
                })}
                {(() => {
                  const parts = (form.userPrincipalName || "").split("@");
                  const prefix = parts[0] || "";
                  const domain = parts[1] || "";
                  const setUpn = (p, d) => setForm({ ...form, userPrincipalName: `${p}@${d}` });
                  const prefixValid = !prefix || /^[a-zA-Z0-9._-]+$/.test(prefix);
                  const changed = (form.userPrincipalName || "").toLowerCase() !== origUpn.toLowerCase();
                  return (
                    <div className="space-y-1.5 md:col-span-2 lg:col-span-3">
                      <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("field.upn")}<span className="text-red-500" data-testid="req-userPrincipalName"> *</span></Label>
                      <div className="flex items-stretch gap-2">
                        <Input
                          value={prefix}
                          maxLength={fieldLimit("upnPrefix")}
                          onChange={(e) => setUpn(e.target.value, domain)}
                          disabled={readOnly}
                          data-testid="attr-userPrincipalName"
                          className="flex-1 rounded-sm border-zinc-300 font-mono focus-visible:ring-2 focus-visible:ring-primary disabled:bg-zinc-50 disabled:text-zinc-400"
                        />
                        <span className="flex items-center px-1 font-mono text-zinc-400">@</span>
                        <Select value={domain} onValueChange={(d) => setUpn(prefix, d)} disabled={readOnly}>
                          <SelectTrigger className="w-64 rounded-sm border-zinc-300 font-mono text-sm disabled:bg-zinc-50" data-testid="attr-upn-domain"><SelectValue placeholder="domain" /></SelectTrigger>
                          <SelectContent>
                            {domains.map((d) => <SelectItem key={d.id} value={d.id}>{d.id}{d.isDefault ? " ·" : ""}</SelectItem>)}
                            {domain && !domains.some((d) => d.id === domain) && <SelectItem value={domain}>{domain}</SelectItem>}
                          </SelectContent>
                        </Select>
                      </div>
                      {prefix && !prefixValid && <p className="text-xs text-red-500" data-testid="attr-upn-invalid">{t("onb.upnInvalid")}</p>}
                      {changed && prefixValid && upnState.checking && <p className="text-xs text-zinc-400" data-testid="attr-upn-checking">{t("onb.upnChecking")}</p>}
                      {changed && prefixValid && !upnState.checking && upnState.available === false && <p className="text-xs font-semibold text-red-500" data-testid="attr-upn-taken">{upnState.reason === "prefix" ? t("onb.prefixTaken") : t("onb.upnTaken")}</p>}
                      {changed && prefixValid && !upnState.checking && upnState.available === true && <p className="text-xs font-semibold text-emerald-600" data-testid="attr-upn-available">{t("onb.upnAvailable")}</p>}
                    </div>
                  );
                })()}
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("field.manager")}{reqFields.includes("manager") && <span className="text-red-500" data-testid="req-manager"> *</span>}</Label>
                  <EmployeePicker
                    value={form.managerId || ""}
                    onChange={(v) => setForm({ ...form, managerId: v })}
                    users={orgUsers}
                    placeholder={t("onb.selectManager")}
                    searchPlaceholder={t("onb.cloneSearch")}
                    emptyText={t("onb.cloneNoMatch")}
                    noneValue="" noneLabel={t("onb.noManager")}
                    testid="attr-manager"
                    disabled={readOnly}
                    className="w-full"
                  />
                </div>
                {!user.isCloudOnly && (
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("field.samAccountName")}<span className="text-red-500" data-testid="req-samAccountName"> *</span></Label>
                    <Input
                      value={form.onPremisesSamAccountName || ""}
                      maxLength={fieldLimit("samAccountName")}
                      onChange={(e) => setForm({ ...form, onPremisesSamAccountName: e.target.value })}
                      disabled={readOnly}
                      data-testid="attr-samAccountName"
                      className={`rounded-sm font-mono text-sm focus-visible:ring-2 focus-visible:ring-primary disabled:bg-zinc-50 disabled:text-zinc-400 ${samState.available === false ? "border-red-400" : "border-zinc-300"}`}
                    />
                    {samState.checking && <p className="text-xs text-zinc-400" data-testid="attr-sam-checking">{t("onb.samChecking")}</p>}
                    {!samState.checking && samState.available === false && <p className="text-xs font-semibold text-red-500" data-testid="attr-sam-taken">{t("onb.samTaken")} {samState.source === "ad" ? t("onb.samInAd") : samState.source === "entra" ? t("onb.samInEntra") : ""}</p>}
                    {!samState.checking && samState.available === true && <p className="text-xs font-semibold text-emerald-600" data-testid="attr-sam-available">{t("onb.samAvailable")}</p>}
                    {samState.available === null && !samState.checking && <p className="text-[11px] text-zinc-400">{t("field.samAccountNameHint")}</p>}
                  </div>
                )}
                <EmailAliasManager
                  value={form.proxyAddresses}
                  onChange={(v) => setForm({ ...form, proxyAddresses: v })}
                  domains={domains}
                  disabled={readOnly}
                  excludeId={id}
                  t={t}
                />
                <div className="space-y-1.5 md:col-span-2 lg:col-span-3">
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("field.description")}</Label>
                  <textarea
                    rows={3}
                    value={form.description || ""}
                    maxLength={fieldLimit("description")}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    disabled={readOnly || (user.isCloudOnly && !cloudDescEnabled)}
                    data-testid="attr-description"
                    className="w-full rounded-sm border border-zinc-300 p-3 text-sm focus-visible:ring-2 focus-visible:ring-primary disabled:bg-zinc-50 disabled:text-zinc-400"
                  />
                  <p className="text-[11px] text-zinc-400">
                    {user.isCloudOnly
                      ? (cloudDescEnabled ? t("field.descCloudLocal") : t("field.descCloudDisabled"))
                      : t("field.descOnPrem")}
                  </p>
                </div>
              </div>
              {!readOnly && (
                <div className="mt-6 flex items-center justify-between border-t border-zinc-100 pt-4" data-testid="employee-visibility-row">
                  <div>
                    <div className="text-sm font-bold text-zinc-900">{t("det.visibilityTitle")}</div>
                    <div className="text-sm text-zinc-500">{t("det.visibilityDesc")}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    {!user.isCloudOnly && (
                      <Button type="button" variant="outline" onClick={verifyAd} data-testid="verify-ad-button" className="rounded-sm border-zinc-300">
                        <Eye size={15} className="mr-1.5" /> {t("det.verifyAd")}
                      </Button>
                    )}
                    <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">{user.isVisible === false ? t("det.hidden") : t("det.visible")}</span>
                    <button type="button" onClick={() => toggleVisible(user.isVisible === false)} data-testid="employee-visible-toggle"
                      title={user.isVisible === false ? t("groups.toggleHidden") : t("groups.toggleVisible")}
                      className={`flex h-9 w-9 items-center justify-center border transition-colors ${user.isVisible === false ? "border-zinc-200 bg-zinc-50 text-zinc-300 hover:text-zinc-500" : "border-emerald-200 bg-emerald-50 text-emerald-600 hover:bg-emerald-100"}`}>
                      {user.isVisible === false ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
              )}
              {!readOnly && (
                <div className="mt-4 flex items-center justify-between border-t border-zinc-100 pt-4" data-testid="employee-gal-row">
                  <div>
                    <div className="text-sm font-bold text-zinc-900">{t("det.galTitle")}</div>
                    <div className="text-sm text-zinc-500">{t("det.galDesc")}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">{user.hiddenFromGal ? t("det.galHidden") : t("det.galVisible")}</span>
                    <Switch checked={!!user.hiddenFromGal} onCheckedChange={toggleGal} data-testid="employee-gal-toggle" />
                  </div>
                </div>
              )}
              <div className="mt-6 flex justify-end">
                <Button onClick={saveAttrs} disabled={saving || readOnly} data-testid="save-attributes" className="rounded-sm bg-primary hover:bg-[#002480]">
                  <Save size={16} className="mr-2" /> {saving ? t("common.saving") : t("det.saveChanges")}
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Access */}
          <TabsContent value="access" className="mt-6">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {apps.length > 0 && (
                <MemberSection
                  title={t("det.applications")} testid="app-section" idKey="id" emptyMsg={t("det.noApplications")}
                  items={userAppIds} refMap={appMap} readOnly={readOnly} available={availApps}
                  onAdd={(aid) => mut(() => api.post(`/users/${id}/applications`, { id: aid }), t("det.appAssigned"))}
                  onRemove={(aid) => mut(() => api.delete(`/users/${id}/applications/${aid}`), t("det.appRemoved"))}
                />
              )}
              <MemberSection
                title={t("det.secGroups")} testid="sec-groups" idKey="id" emptyMsg={t("det.noSecGroups")}
                items={userSec} refMap={groupMap} readOnly={readOnly}
                available={secGroups.filter((g) => !memberOf.includes(g.id) && g.isVisible !== false && !(hybrid && user.isCloudOnly && g.isOnPrem))}
                onAdd={(gid) => mut(() => api.post(`/users/${id}/groups`, { id: gid }), t("det.addedGroup"))}
                onRemove={(gid) => mut(() => api.delete(`/users/${id}/groups/${gid}`), t("det.removedGroup"))}
              />
              <MemberSection
                title={t("det.m365Groups")} testid="m365-groups" idKey="id" emptyMsg={t("det.noM365Groups")}
                items={userM365} refMap={groupMap} readOnly={readOnly}
                available={m365Groups.filter((g) => !memberOf.includes(g.id) && g.isVisible !== false)}
                onAdd={(gid) => mut(() => api.post(`/users/${id}/groups`, { id: gid }), t("det.addedGroup"))}
                onRemove={(gid) => mut(() => api.delete(`/users/${id}/groups/${gid}`), t("det.removedGroup"))}
              />
              <MemberSection
                title={t("det.distLists")} testid="dist-lists" idKey="id" emptyMsg={t("det.noDistLists")}
                items={userDist} refMap={groupMap} readOnly={readOnly}
                available={distGroups.filter((g) => !memberOf.includes(g.id) && g.isVisible !== false)}
                onAdd={(gid) => mut(() => api.post(`/users/${id}/groups`, { id: gid }), t("det.addedList"))}
                onRemove={(gid) => mut(() => api.delete(`/users/${id}/groups/${gid}`), t("det.removedList"))}
              />
              <MemberSection
                title={t("lic.title")} testid="lic-section" idKey="id" emptyMsg={t("det.noLicenses")}
                items={user.assignedLicenses || []} refMap={licMap} readOnly={readOnly}
                available={licDisplay.filter((l) => !(user.assignedLicenses || []).includes(l.skuId) && l.consumedUnits < l.totalUnits && !groupManagedSkus.has(l.skuId))}
                onAdd={(sku) => mut(() => api.post(`/users/${id}/licenses`, { id: sku }), t("det.licenseAssigned"))}
                onRemove={(sku) => mut(() => api.delete(`/users/${id}/licenses/${sku}`), t("det.licenseRemoved"))}
              />
              <MemberSection
                title={t("det.teams")} testid="teams-section" idKey="id" emptyMsg={t("det.noTeams")}
                items={user.teamMemberships || []} refMap={teamMap} readOnly={readOnly}
                available={teams.filter((tm) => !(user.teamMemberships || []).includes(tm.id) && tm.isVisible !== false)}
                onAdd={(tid) => mut(() => api.post(`/users/${id}/teams`, { id: tid }), t("det.addedTeam"))}
                onRemove={(tid) => mut(() => api.delete(`/users/${id}/teams/${tid}`), t("det.removedTeam"))}
              />
              {userDynamic.length > 0 && (
                <div data-testid="employee-dynamic-preview">
                  <div className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.15em] text-violet-700"><Sparkles size={13} /> {t("access.autoTitle")}</div>
                  <p className="mb-2 text-[11px] text-zinc-400">{t("access.autoHint")}</p>
                  <div className="space-y-1.5">
                    {userDynamic.map((gid) => {
                      const g = groupMap[gid] || {};
                      return (
                        <div key={gid} className="flex items-start justify-between gap-3 border border-violet-200 bg-violet-50/60 px-3 py-2" data-testid={`employee-dyn-${gid}`}>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 text-sm font-medium text-zinc-800">{g.displayName || gid}
                              <span className="border border-violet-300 bg-violet-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-violet-700">{t("access.autoBadge")}</span></div>
                            {(g.membershipRule || g.recipientFilter) && <div className="mt-0.5 break-all font-mono text-[10px] text-zinc-500">{g.membershipRule || g.recipientFilter}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Org / Team */}
          <TabsContent value="org" className="mt-6">
            <div className="border border-zinc-200 bg-white p-6" data-testid="org-chart">
              <h2 className="mb-6 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-zinc-600"><Network size={14} /> {t("det.orgChart")}</h2>
              <div className="flex flex-col items-center">
                {/* Manager (up) */}
                {user.managerId ? (
                  <>
                    <Link to={`/employees/${user.managerId}`} data-testid="org-manager"
                      className="group flex items-center gap-3 border border-zinc-200 bg-zinc-50 px-4 py-2.5 transition-colors hover:border-primary hover:bg-primary/5">
                      <span className="flex h-9 w-9 items-center justify-center bg-zinc-700 text-xs font-bold text-white">{initials(user.managerName)}</span>
                      <span className="text-left">
                        <span className="block text-[10px] font-bold uppercase tracking-wider text-zinc-400">{t("field.manager")}</span>
                        <span className="block text-sm font-semibold text-zinc-900 group-hover:text-primary">{user.managerName || user.managerId}</span>
                      </span>
                    </Link>
                    <ChevronDown size={18} className="my-1 text-zinc-300" />
                  </>
                ) : <p className="mb-3 text-xs text-zinc-400" data-testid="org-no-manager">{t("det.noManagerSet")}</p>}

                {/* Current employee (center) */}
                <div className="flex items-center gap-3 border-2 border-primary bg-primary/5 px-5 py-3" data-testid="org-self">
                  <span className="flex h-11 w-11 items-center justify-center bg-primary text-sm font-bold text-white">{initials(user.displayName)}</span>
                  <div className="text-left">
                    <div className="text-sm font-bold text-zinc-950">{user.displayName}</div>
                    <div className="text-xs text-zinc-500">{user.jobTitle || "—"}</div>
                  </div>
                </div>

                {/* Direct reports (down) */}
                {(user.directReports || []).length > 0 ? (
                  <>
                    <ChevronDown size={18} className="my-1 text-zinc-300" />
                    <div className="mb-3 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-zinc-500">
                      <Users2 size={13} /> {t("det.directReports")} · {user.directReports.length}
                    </div>
                    <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {user.directReports.map((r) => (
                        <Link key={r.id} to={`/employees/${r.id}`} data-testid={`org-report-${r.id}`}
                          className="group flex items-center gap-3 border border-zinc-200 bg-white px-4 py-2.5 transition-colors hover:border-primary hover:bg-primary/5">
                          <span className="relative flex h-9 w-9 shrink-0 items-center justify-center bg-zinc-900 text-xs font-bold text-white">
                            {initials(r.displayName)}
                            <span className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 border-2 border-white ${r.accountEnabled === false ? "bg-zinc-400" : "bg-emerald-500"}`} />
                          </span>
                          <span className="min-w-0 text-left">
                            <span className="block truncate text-sm font-semibold text-zinc-900 group-hover:text-primary">{r.displayName}</span>
                            <span className="block truncate text-xs text-zinc-500">{r.jobTitle || "—"}</span>
                          </span>
                        </Link>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="mt-4 text-sm text-zinc-400" data-testid="org-no-reports">{t("det.noReports")}</p>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Lifecycle / danger */}
          <TabsContent value="danger" className="mt-6">
            {gaProtected ? (
              <div className="flex items-center gap-3 border border-amber-300 bg-amber-50 p-6" data-testid="lifecycle-readonly">
                <ShieldAlert size={20} className="shrink-0 text-amber-600" />
                <div className="text-sm text-amber-900">{t("det.gaProtectedDesc")}</div>
              </div>
            ) : !canLifecycle ? (
              <div className="flex items-center gap-3 border border-zinc-200 bg-zinc-50 p-6" data-testid="lifecycle-noperm">
                <ShieldAlert size={20} className="shrink-0 text-zinc-400" />
                <div className="text-sm text-zinc-500">You don't have permission to perform lifecycle actions on this account.</div>
              </div>
            ) : (
            <div className="space-y-5">
              {canEnableDisable && (
              <div className="flex flex-wrap items-center justify-between gap-4 border border-zinc-200 bg-white p-5">
                <div>
                  <div className="text-sm font-bold text-zinc-900">{user.status === "disabled" ? t("det.enableAccount") : t("det.disableAccount")}</div>
                  <div className="text-sm text-zinc-500">{user.status === "disabled" ? t("det.enableDesc") : t("det.disableDesc")}</div>
                </div>
                <Button onClick={toggleEnable} variant="outline" data-testid="toggle-enable" className="rounded-sm border-zinc-300">
                  {user.status === "disabled" ? <><Power size={16} className="mr-2" /> {t("det.enable")}</> : <><PowerOff size={16} className="mr-2" /> {t("det.disable")}</>}
                </Button>
              </div>
              )}

              {canManage && (
              <div className="flex flex-wrap items-center justify-between gap-4 border border-zinc-200 bg-white p-5" data-testid="role-apply-card">
                <div>
                  <div className="text-sm font-bold text-zinc-900">{t("roleApply.cardTitle")}</div>
                  <div className="text-sm text-zinc-500">{t("roleApply.cardDesc")}</div>
                </div>
                <Button onClick={() => setRoleOpen(true)} variant="outline" data-testid="open-role-apply" className="rounded-sm border-zinc-300">
                  <Layers size={16} className="mr-2" /> {t("roleApply.applyBtn")}
                </Button>
              </div>
              )}

              {canReset && (
              <div className="flex flex-wrap items-center justify-between gap-4 border border-zinc-200 bg-white p-5">
                <div>
                  <div className="text-sm font-bold text-zinc-900">{t("pw.cardTitle")}</div>
                  <div className="text-sm text-zinc-500">{hybrid && user.isCloudOnly ? t("pw.cardDescCloud") : t("pw.cardDesc")}</div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button onClick={() => setPwOpen(true)} variant="outline" data-testid="open-reset-password" className="rounded-sm border-zinc-300">
                    <KeyRound size={16} className="mr-2" /> {tapEnabled && user.isCloudOnly ? t("pw.tapCreate") : t("pw.reset")}
                  </Button>
                  {tapEnabled && user.isCloudOnly && (
                    <Button onClick={() => setReissueOpen(true)} variant="outline" data-testid="open-reissue-tap" className="rounded-sm border-zinc-300">
                      <RefreshCw size={16} className="mr-2" /> {t("pw.reissueCta")}
                    </Button>
                  )}
                </div>
              </div>
              )}

              {canOffboard && (
              <div className="flex flex-wrap items-center justify-between gap-4 border border-amber-200 bg-amber-50/60 p-5">
                <div className="flex items-start gap-3">
                  <CalendarClock size={20} className="mt-0.5 text-amber-600" />
                  <div>
                    <div className="text-sm font-bold text-amber-900">{t("det.offboardTitle")}</div>
                    <div className="text-sm text-amber-800/80">{t("det.offboardDesc")}</div>
                  </div>
                </div>
                <Dialog open={offOpen} onOpenChange={setOffOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="open-offboard" className="rounded-sm bg-amber-600 text-white hover:bg-amber-700">{t("det.offboardBtn")}</Button>
                  </DialogTrigger>
                  <DialogContent className="rounded-sm sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle className="font-display uppercase tracking-tight">{t("det.offboardHeading")} {user.givenName}</DialogTitle>
                      <DialogDescription>{t("det.offboardDialogDesc")}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                      <div className="flex border border-zinc-200">
                        {["immediate", "scheduled"].map((m) => (
                          <button
                            key={m} onClick={() => setOff({ ...off, mode: m })} data-testid={`offboard-mode-${m}`}
                            className={`flex-1 px-3 py-2 text-xs font-bold uppercase tracking-wider ${off.mode === m ? "bg-zinc-900 text-white" : "bg-white text-zinc-500 hover:bg-zinc-100"}`}
                          >{m === "immediate" ? t("det.modeImmediate") : t("det.modeScheduled")}</button>
                        ))}
                      </div>
                      {off.mode === "scheduled" && (
                        <div className="space-y-1.5">
                          <Label className="text-xs font-bold uppercase tracking-wider text-amber-700">{t("det.leaveDate")}</Label>
                          <Input type="date" value={off.leaveDate} onChange={(e) => setOff({ ...off, leaveDate: e.target.value })}
                            data-testid="offboard-leave-date" className="rounded-sm border-amber-300" />
                        </div>
                      )}
                      {[["blockSignIn", t("det.blockSignIn")], ["removeLicenses", t("det.removeLicenses")], ["removeFromGroups", t("det.removeGroups")], ["removeFromTeams", t("det.removeTeams")]].map(([k, lbl]) => (
                        <label key={k} className="flex items-center gap-2.5 text-sm text-zinc-700">
                          <Checkbox checked={off[k]} onCheckedChange={(v) => setOff({ ...off, [k]: !!v })} data-testid={`offboard-${k}`} />
                          {lbl}
                        </label>
                      ))}
                      {off.mode === "immediate" && (
                        <label className="flex items-center gap-2.5 text-sm font-semibold text-red-700">
                          <Checkbox checked={off.delete} onCheckedChange={(v) => setOff({ ...off, delete: !!v })} data-testid="offboard-delete" />
                          {t("det.permDelete")}
                        </label>
                      )}
                    </div>
                    <DialogFooter>
                      <Button variant="ghost" onClick={() => setOffOpen(false)} className="rounded-sm">{t("common.cancel")}</Button>
                      <Button onClick={submitOffboard} data-testid="confirm-offboard" className="rounded-sm bg-amber-600 text-white hover:bg-amber-700">
                        <ShieldAlert size={16} className="mr-2" /> {t("det.confirm")}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
              )}

              {canOffboard && (
              <div className="flex flex-wrap items-center justify-between gap-4 border border-red-200 bg-red-50/50 p-5">
                <div>
                  <div className="text-sm font-bold text-red-900">{t("det.deleteAccount")}</div>
                  <div className="text-sm text-red-800/70">{t("det.deleteDesc")}</div>
                </div>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="destructive" data-testid="open-delete" className="rounded-sm"><Trash2 size={16} className="mr-2" /> {t("det.deleteConfirm")}</Button>
                  </DialogTrigger>
                  <DialogContent className="rounded-sm sm:max-w-sm">
                    <DialogHeader>
                      <DialogTitle className="font-display uppercase tracking-tight">{t("det.deleteConfirm")} {user.displayName}?</DialogTitle>
                      <DialogDescription>{t("det.deleteDialogDesc")}</DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                      <Button onClick={() => mut(() => api.delete(`/users/${id}`).then(() => nav("/employees")), t("det.userDeleted"))}
                        variant="destructive" data-testid="confirm-delete" className="rounded-sm">{t("det.yesDelete")}</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
              )}
            </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
      <ResetPasswordDialog open={pwOpen} onOpenChange={setPwOpen} userId={id} employeeName={user.displayName} isCloudOnly={!!user.isCloudOnly} />
      <ReissueTapDialog open={reissueOpen} onOpenChange={setReissueOpen} userId={id} employeeName={user.displayName} />
      <RoleTemplateApplyDialog open={roleOpen} onOpenChange={setRoleOpen} uid={id} onApplied={load} />
      <Dialog open={verifyOpen} onOpenChange={setVerifyOpen}>
        <DialogContent className="rounded-sm" data-testid="verify-ad-dialog">
          <DialogHeader>
            <DialogTitle>{t("det.verifyAdTitle")}</DialogTitle>
            <DialogDescription>{t("det.verifyAdDesc")}</DialogDescription>
          </DialogHeader>
          {verifying && (
            <div className="flex items-center gap-2 py-6 text-sm text-zinc-500" data-testid="verify-ad-loading">
              <Loader2 size={16} className="animate-spin" /> {t("det.verifyAdRunning")}
            </div>
          )}
          {!verifying && verifyRes?.error && (
            <div className="border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="verify-ad-error">{verifyRes.error}</div>
          )}
          {!verifying && verifyRes?.note && (
            <div className="space-y-2" data-testid="verify-ad-note">
              <div className="border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">{t("det.verifyNoAttrs")}</div>
              <div className="border border-zinc-200 bg-zinc-50 p-2 font-mono text-xs text-zinc-600">{verifyRes.note}{verifyRes.raw ? ` — ${JSON.stringify(verifyRes.raw)}` : ""}</div>
            </div>
          )}
          {!verifying && verifyRes?.attributes && (
            <div className="max-h-80 overflow-auto border border-zinc-200" data-testid="verify-ad-result">
              <table className="w-full text-left text-sm">
                <tbody>
                  {Object.entries(verifyRes.attributes).map(([k, v]) => (
                    <tr key={k} className="border-b border-zinc-100 last:border-0">
                      <td className="w-1/3 bg-zinc-50 px-3 py-2 font-mono text-xs font-bold text-zinc-500">{k}</td>
                      <td className="px-3 py-2 text-zinc-800" data-testid={`verify-attr-${k}`}>{v === null || v === "" ? "—" : String(v)}</td>
                    </tr>
                  ))}
                  {Object.keys(verifyRes.attributes).length === 0 && (
                    <tr><td className="px-3 py-3 text-zinc-400">{t("det.verifyAdEmpty")}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <AiResultDialog open={aiRunner.state.open} onOpenChange={(v) => !v && aiRunner.close()} title={aiRunner.state.title} loading={aiRunner.state.loading} error={aiRunner.state.error} content={aiRunner.state.content} sources={aiRunner.state.sources} empty={aiRunner.state.empty} />
    </>
  );
}
